import debounce from 'lodash.debounce'
import { Logger } from 'typescript-log'
import { GptAdSlotDefinition } from '../advertising/gpt-ad-slot-definintion'
import { SizeMapping, uniqueSlotSizes } from '../advertising/size-mapping'
import { isServerEnvironment } from '../environment'

export interface MediaQueryMap {
    [width: number]: {
        mq: MediaQueryList
        slotIds: string[]
    }
}

export interface SetSlotAdditionalTargetingOptions {
    gptSlots: googletag.Slot[]
    slots: GptAdSlotDefinition[]
    gptApi: GptApi
}

export interface GptApiOptions {
    log: Logger

    /** Loads the gpt script and waits for it to be initialised successfully */
    loadGptLibrary: () => Promise<GptLibrary | false>

    /**
     * Called on a new page to allow setting of additional page targeting
     *
     * @param cancelled Should be checked before setting targeting if the targeting info was loaded asynchronously
     **/
    setAdditionalPageTargeting?: (options: {
        cancelled: () => boolean
        gptApi: GptApi
        gptLibrary: GptLibrary
    }) => Promise<void>

    /**
     * Called just before slots are refreshed to set additional targeting on the slot itself
     * @param cancelled Should be checked before setting targeting info */
    setAdditionalSlotTargeting?: (
        params: SetSlotAdditionalTargetingOptions,
    ) => void

    /**
     * Called after ad slots are cleaned up to perform any additional cleanup
     **/
    performAdditionalCleanup?: (gptApi: GptApi) => void

    requestIdleCallback?: typeof window.requestIdleCallback

    enableCompanionAds?: boolean
}

export interface SlotRenderEndedEvent
    extends googletag.events.SlotRenderEndedEvent {
    unit: GptAdSlotDefinition
}
export type SlotRenderEndedEventHandler = (event: SlotRenderEndedEvent) => void

export interface SlotMap {
    [id: string]: {
        gptSlot: googletag.Slot
        definition: GptAdSlotDefinition
    }
}

// To reduce mocking, pick just what we need
export type GptLibrary = Pick<
    typeof googletag,
    | 'defineSlot'
    | 'apiReady'
    | 'pubadsReady'
    | 'destroySlots'
    | 'enableServices'
    | 'defineOutOfPageSlot'
    | 'sizeMapping'
    | 'companionAds'
    | 'display'
> & {
    pubads: () => Pick<
        googletag.PubAdsService,
        'clear' | 'addEventListener' | 'refresh' | 'setTargeting'
    >
}

/**
 * Wrapper around GPT which sets targeting info and refreshes slots when
 * their size mappings change
 */
export class GptApi {
    displayedSlots: SlotMap = {}
    mediaQueries: MediaQueryMap = {}
    gptLibrary: GptLibrary | undefined
    private loadingGptLibrary: Promise<void>
    private initialised = false
    private slotRenderEndedEventHandler: SlotRenderEndedEventHandler | undefined
    private refreshAllSlots = debounce(() => {
        const slotIds = Object.keys(this.displayedSlots)
        const displayedSlots = slotIds.map((id) => this.displayedSlots[id])
        const gptSlots = displayedSlots.map((slot) => slot.gptSlot)
        const slots = displayedSlots.map((slot) => slot.definition)
        this.clearSlots(gptSlots)
        const ric =
            this.options.requestIdleCallback || window.requestIdleCallback
        ric(() => this.refreshSlots(slots, gptSlots), { timeout: 300 })
    }, 250)
    companionAdsEnabled = false

    constructor(private options: GptApiOptions) {
        if (!isServerEnvironment()) {
            this.companionAdsEnabled = options.enableCompanionAds ?? false
            options.log.debug('GPT API: Loading GPT')
            this.loadingGptLibrary = options
                .loadGptLibrary()
                .then(async (gpt) => {
                    if (gpt) {
                        const pubAds = gpt.pubads()
                        pubAds.addEventListener('slotRenderEnded', (event) => {
                            const idArray = event.slot.getTargeting('slotId')
                            this.options.log.debug(
                                {
                                    id: idArray ? idArray[0] : 'unknown',
                                    isEmpty: event.isEmpty,
                                    event,
                                },
                                'GPT Api: Ad rendered',
                            )
                            if (this.slotRenderEndedEventHandler) {
                                this.slotRenderEndedEventHandler(
                                    event as SlotRenderEndedEvent,
                                )
                            }
                        })
                        /**
                         * At this point:
                         * - gpt.js has loaded and initialised
                         * - gpt.js API methods are ready to be called
                         * - gpt.js PubAds service methods are ready to be called
                         */
                    }

                    return gpt
                })
                .then((gpt) => {
                    if (gpt) {
                        options.log.debug(
                            'GPT API: GPT Loaded and initialised successfully',
                        )
                        this.gptLibrary = gpt
                    } else {
                        options.log.info('GPT API: GPT not loaded')
                    }
                })
        } else {
            this.loadingGptLibrary = Promise.resolve()
        }
    }

    /**
     * Idempotent function to ensure GPT library is loaded, initialised and the API and PubAds services are ready!
     */
    async ensureInitialised() {
        if (this.initialised) {
            return
        }
        await this.loadingGptLibrary
        this.initialised = true
    }

    /**
     * changeCorrelator: boolean - changeCorrelator specifies whether or not a new correlator is to be generated for fetching ads
     * Google's servers maintain the Correlator (~30 seconds) so this isn't guaranteed depending on timing
     * https://developers.google.com/doubleclick-gpt/reference#googletag.PubAdsService_refresh
     */
    displaySlots(slots: GptAdSlotDefinition[], changeCorrelator = false) {
        this.options.log.debug(
            { slots, changeCorrelator },
            'GPT Api: Displaying slots',
        )

        const gptSlots = slots
            .map((slot) => this.defineGptSlot(slot))
            .filter((slot): slot is googletag.Slot => !!slot)

        if (this.options.setAdditionalSlotTargeting) {
            this.options.log.debug(
                { slots, changeCorrelator },
                'GPT Api: Setting additional slot targeting',
            )
            this.options.setAdditionalSlotTargeting({
                gptApi: this,
                gptSlots: gptSlots,
                slots: slots,
            })
        }
        this.gptLibrary?.pubads().refresh(gptSlots, { changeCorrelator })

        if (this.gptLibrary && this.companionAdsEnabled) {
            try {
                googletag
                    .companionAds()
                    .getSlots()
                    .forEach((slot) =>
                        this.options.log.debug(
                            {
                                id: slot.getSlotElementId(),
                                adUnitPath: slot.getAdUnitPath(),
                                attributeKeys: slot.getAttributeKeys(),
                                categoryExclusions:
                                    slot.getCategoryExclusions(),
                                responseInformation:
                                    slot.getResponseInformation(),
                                targetingKeys: slot.getTargetingKeys(),
                                gootestTargeting: slot.getTargeting('gootest'),
                            },
                            'GPT API: companion-dump ' +
                                slot.getSlotElementId(),
                        ),
                    )
            } catch (e) {
                this.options.log.debug(
                    { err: e },
                    'GPT API: failed companion-dump',
                )
            }
        }
    }

    async setPageAdditionalTargeting(cancelled: () => boolean) {
        await this.ensureInitialised()
        if (this.options.setAdditionalPageTargeting && this.gptLibrary) {
            await this.options.setAdditionalPageTargeting({
                cancelled,
                gptApi: this,
                gptLibrary: this.gptLibrary,
            })
        }
    }

    destroySlots(slots: GptAdSlotDefinition[]) {
        this.options.log.debug({ slots }, 'GPT Api: Destroying slots')

        const slotsToDestroy = slots
            .map((slot) => {
                const displayedSlot = this.displayedSlots[slot.id]
                if (!displayedSlot) {
                    return undefined
                }
                const gptSlot = displayedSlot.gptSlot
                delete this.displayedSlots[slot.id]
                if (slot.sizeMapping) {
                    this.removeMediaQuery(slot.id, slot.sizeMapping)
                }
                return gptSlot
            })
            .filter((slot): slot is googletag.Slot => slot !== undefined)
        if (!slotsToDestroy.length) {
            return
        }
        this.gptLibrary?.destroySlots(slotsToDestroy)
    }

    registerOnSlotRenderEnded(handler: SlotRenderEndedEventHandler) {
        this.slotRenderEndedEventHandler = handler
    }

    private clearSlots(slots?: googletag.Slot[]) {
        this.gptLibrary?.pubads()?.clear(slots)
        if (this.options.performAdditionalCleanup) {
            this.options.log.debug('GPT API: Performing additional ad cleanup')
            this.options.performAdditionalCleanup(this)
        }
    }

    private tryDefineSlot(
        slot: GptAdSlotDefinition,
        isRetry: boolean,
    ): googletag.Slot | null {
        if (!this.gptLibrary) {
            return null
        }

        let gptSlot
        if (slot.outOfPage) {
            gptSlot = this.gptLibrary.defineOutOfPageSlot(
                slot.adUnitPath,
                slot.id,
            )
        } else {
            // The value below is used when there are no size mappings, or the viewport width is below all specified size mappings.
            // We always cover all breakpoints in our size mappings so this fallback should not be used
            // https://developers.google.com/doubleclick-gpt/reference#googletag.defineSlot
            const sizes = uniqueSlotSizes(slot.sizeMapping)
            this.options.log.debug(
                { id: slot.id, sizes },
                'GPT API: Defining slot with fallback sizes',
            )
            gptSlot = this.gptLibrary.defineSlot(
                slot.adUnitPath,
                sizes,
                slot.id,
            )
        }

        // Don't retry when adUnitPath is empty, it will fail
        if (gptSlot || isRetry || slot.adUnitPath === '') {
            return gptSlot
        }

        /**
         * At this point, Googletag has returned `null`, which means a slot with
         * the same element id still exists in Googletag's internal slot list.
         */
        this.options.log.warn(
            { id: slot.id, slot },
            'GPT Api: slot could not be defined, retrying! (duplicate id?)',
        )

        const slotToDestroy = Object.keys(this.displayedSlots)
            .filter((id) => id === slot.id)
            .map((id) => this.displayedSlots[id].gptSlot)
        this.gptLibrary.destroySlots(slotToDestroy)
        return this.tryDefineSlot(slot, true)
    }

    private defineGptSlot(slot: GptAdSlotDefinition): googletag.Slot | null {
        if (!this.gptLibrary) {
            return null
        }

        const gptSlot = this.tryDefineSlot(slot, false)
        if (!gptSlot) {
            // Static pages do not have ad unit paths
            if (slot.adUnitPath !== '') {
                this.options.log.error(
                    { id: slot.id, slot },
                    'GPT Api: slot could not be defined! (duplicate id?)',
                )
            }
            return gptSlot
        }

        /**
         * Handle ad exclusion labels: https://support.google.com/dfp_premium/answer/3238504?hl=en
         * This is required for scenarios such as: a slot that only serves a specific campaign that
         * should NOT have programmatic/house/paid ads so that it can auto-collapse when the campaign
         * is not running.
         */
        const { exclusionLabels = [] } = slot
        for (const label of exclusionLabels) {
            gptSlot.setCategoryExclusion(label)
        }
        const targeting = slot.targeting
        if (targeting) {
            Object.keys(targeting).forEach((key) => {
                gptSlot.setTargeting(key, targeting[key])
            })
        }

        if (slot.sizeMapping) {
            // Tests don't have clientWidth, so default to innerWidth
            const clientWidth =
                document.documentElement.clientWidth || window.innerWidth
            const scrollbarWidth = window.innerWidth - clientWidth
            let smbuilder = this.gptLibrary.sizeMapping()
            let sizeAdded = false
            slot.sizeMapping.forEach((mapping) => {
                // It appears that GPT works on width excluding scrollbars, but
                // breakpoints include scrollbars. This takes this into account
                let width = mapping.viewport[0] - scrollbarWidth
                if (width < 0) {
                    width = 0
                }

                // It's invalid to specify a size mapping but have no sizes.
                // We also would unmount this ad slot when the viewport is increased
                if (mapping.slot.length > 0) {
                    const height = mapping.viewport[1]
                    smbuilder = smbuilder.addSize([width, height], mapping.slot)
                    sizeAdded = true
                }
            })

            if (!sizeAdded) {
                this.options.log.warn(
                    { id: slot.id, slot },
                    'GPT API: Empty size mapping',
                )
            }

            const sizeMapping = smbuilder.build()
            gptSlot.defineSizeMapping(sizeMapping)
            this.addMediaQuery(slot.id, slot.sizeMapping)

            this.options.log.debug(
                { id: slot.id, slot, sizeMapping },
                'GPT Api: Defined GPT slot with size mapping',
            )
        } else {
            this.options.log.warn(
                { id: slot.id, slot },
                'GPT API: No size mapping found',
            )
        }
        if (slot.forceSafeFrame !== undefined) {
            // It appears that some ad blockers you still get an ad slot object,
            // but it does not have a `setForceSafeFrame` function on it
            if (gptSlot.setForceSafeFrame) {
                gptSlot.setForceSafeFrame(slot.forceSafeFrame)
            }
        }

        if (this.companionAdsEnabled && slot.companion) {
            this.options.log.info(
                { id: slot.id, slot },
                'GPT Api: gptSlot.addService(this.gpt.companionAds())',
            )
            gptSlot.addService(this.gptLibrary.companionAds())
        }
        gptSlot.addService(this.gptLibrary.pubads() as any)
        // this does not actually display the slot as we set disableInitialLoad()
        // we have to call refresh() to actually display it

        this.options.log.debug({ id: slot.id, slot }, 'GPT Api: Display slot')
        this.gptLibrary.display(slot.id)
        this.displayedSlots[slot.id] = {
            gptSlot,
            definition: slot,
        }
        return gptSlot
    }

    private addMediaQuery(slotId: string, sizeMapping: SizeMapping[]) {
        if (typeof window === 'undefined' || window.matchMedia === undefined) {
            return
        }
        sizeMapping.forEach((mapping) => {
            const width = mapping.viewport[0]
            if (width === 0) {
                return
            }
            const existingContainer = this.mediaQueries[width]
            if (existingContainer) {
                if (existingContainer.slotIds.indexOf(slotId) === -1) {
                    existingContainer.slotIds.push(slotId)
                }
                return
            }
            const mqContainer = {
                mq: window.matchMedia(`(min-width: ${width}px)`),
                slotIds: [slotId],
            }
            mqContainer.mq.addListener(this.refreshAllSlots)
            this.mediaQueries[width] = mqContainer
        })
    }

    private removeMediaQuery(slotId: string, sizeMapping: SizeMapping[]) {
        if (typeof window === 'undefined' || window.matchMedia === undefined) {
            return
        }
        sizeMapping.forEach((mapping) => {
            const width = mapping.viewport[0]
            const mqContainer = this.mediaQueries[width]
            if (width === 0 || !mqContainer) {
                return
            }
            const index = mqContainer.slotIds.indexOf(slotId)
            if (index !== -1) {
                mqContainer.slotIds.splice(index, 1)
            }
            if (mqContainer.slotIds.length > 0) {
                return
            }
            mqContainer.mq.removeListener(this.refreshAllSlots)
            delete this.mediaQueries[width]
        })
    }

    private refreshSlots(
        slots: GptAdSlotDefinition[],
        gptSlots?: googletag.Slot[],
    ) {
        if (gptSlots) {
            if (this.options.setAdditionalSlotTargeting) {
                this.options.setAdditionalSlotTargeting({
                    gptApi: this,
                    gptSlots,
                    slots,
                })
            }

            this.gptLibrary
                ?.pubads()
                ?.refresh(gptSlots, { changeCorrelator: false })
        }
    }
}
